import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Baby, Plus, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePreschoolUploads } from "@/context/PreschoolUploadContext";
import { PreschoolCreateForm } from "@/components/preschool/PreschoolCreateForm";
import { PreschoolTaskCard } from "@/components/preschool/PreschoolTaskCard";
import { NumbersReviewDialog } from "@/components/preschool/NumbersReviewDialog";
import { ResultsViewer } from "@/components/preschool/ResultsViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/preschool`;

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

export default function PreschoolToolPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { uploads, enqueueTask, cancelUpload } = usePreschoolUploads();
  const [tasks, setTasks] = useState([]);
  const [creating, setCreating] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tasks`);
      setTasks(data.tasks);
    } catch { /* keep last list */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const serverIds = new Set(tasks.map((t) => t.task_id));
  const localRows = Object.entries(uploads)
    .filter(([id]) => !serverIds.has(id))
    .map(([id, u]) => ({ task_id: id, name: u.name, status: u.status === "done" ? "queued" : u.status, local: true,
      num_photos: u.total - 2, is_own: true, progress: { pct: Math.round((100 * u.done) / Math.max(1, u.total)), message: `Uploading ${u.done}/${u.total}` } }));
  const merged = tasks.map((t) => (uploads[t.task_id] ? { ...t, localUpload: uploads[t.task_id] } : t));
  const all = [...localRows, ...merged];

  const onDownload = async (task) => {
    try {
      const { data } = await axios.get(`${API}/tasks/${task.task_id}/download`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };
  const onLog = async (task) => {
    try {
      const res = await axios.get(`${API}/tasks/${task.task_id}/log`, { responseType: "blob" });
      const safe = (task.name || "preschool").replace(/[^A-Za-z0-9._\- ]+/g, "").trim().replace(/\s+/g, "_");
      saveBlob(res.data, `${safe}_Preschool.log`);
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Task not found" : "Log download failed");
    }
  };
  const onRetry = async (task) => {
    await axios.post(`${API}/tasks/${task.task_id}/retry`).catch((e) => toast.error(e.response?.data?.detail || "Retry failed"));
    load();
  };
  const onApprove = async (task) => {
    try {
      await axios.post(`${API}/tasks/${task.task_id}/approve`);
      toast.success("Approved — building the ZIP");
      setViewing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    }
  };
  const onDelete = async (task) => {
    if (!window.confirm(`${task.status === "uploading" ? "Cancel upload of" : "Delete"} "${task.name}"?`)) return;
    cancelUpload(task.task_id);
    if (!task.local) {
      try {
        await axios.delete(`${API}/tasks/${task.task_id}`);
      } catch (e) {
        if (e.response?.status !== 404) return toast.error(e.response?.data?.detail || "Delete failed");
      }
    }
    setTasks((prev) => prev.filter((t) => t.task_id !== task.task_id));
    toast.success("Task deleted");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="preschool-tool-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-slate-500 hover:text-slate-300 inline-flex items-center gap-1" data-testid="preschool-back">
              <ArrowLeft className="h-4 w-4" /><span className="text-[11px] uppercase tracking-widest">Back</span>
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Baby className="h-5 w-5 text-amber-400" />Preschool Tool
                {isAdmin && (
                  <span className="inline-flex items-center gap-1 px-2 h-6 rounded-sm border border-indigo-800/60 bg-indigo-950/40 text-indigo-200 text-[10px] font-bold uppercase tracking-widest" data-testid="preschool-admin-badge">
                    <Shield className="h-3 w-3" />Admin · All Users
                  </span>
                )}
              </h1>
              <p className="text-[11px] text-slate-500 mt-0.5">Sheet numbers → named photos, face-checked, sorted by class.</p>
            </div>
          </div>
          <button type="button" onClick={() => setCreating(true)} data-testid="preschool-new-btn"
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-md bg-amber-400 text-slate-950 text-[12px] font-bold uppercase tracking-widest hover:bg-amber-300 hover:-translate-y-0.5 transition-[transform,background-color]">
            <Plus className="h-4 w-4" />New task
          </button>
        </div>

        {creating && (
          <PreschoolCreateForm onClose={() => setCreating(false)}
            onSubmit={(p) => { enqueueTask(p); setCreating(false); }} />
        )}

        <div className="space-y-3" data-testid="preschool-task-list">
          {all.length === 0 && (
            <div className="border border-dashed border-slate-800 rounded-md p-10 text-center text-[12px] text-slate-500" data-testid="preschool-empty">
              No preschool tasks yet. Start one with the sheet, names file and photo folders.
            </div>
          )}
          {all.map((t) => (
            <PreschoolTaskCard key={t.task_id} task={t} onReview={setReviewing} onView={setViewing}
              onDownload={onDownload} onLog={onLog} onRetry={onRetry} onDelete={onDelete} onApprove={onApprove} />
          ))}
        </div>
      </div>
      {reviewing && (
        <NumbersReviewDialog task={reviewing} onClose={() => setReviewing(null)}
          onConfirmed={() => { setReviewing(null); load(); }} />
      )}
      {viewing && <ResultsViewer task={viewing} onClose={() => { setViewing(null); load(); }} onDownload={onDownload} onApprove={onApprove} />}
    </div>
  );
}
